import { Component } from '@theme/component';
import { CartLinesUpdateEvent } from '@shopify/events';
import { buildSteeringOrder, reconcileSteeringCart, steeringEndOptions } from '@theme/dor-steering-model';

class DORSteeringBuilder extends Component {
  requiredRefs = ['form', 'summary', 'total', 'submitButton', 'status'];
  #config;
  #controller;
  #busy = false;
  #uncertain = false;
  #selections = { track: {}, drag: {} };

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    try {
      this.#config = JSON.parse(this.querySelector('[data-steering-config]').textContent);
      this.#config.root = this.#config.root.endsWith('/') ? this.#config.root : `${this.#config.root}/`;
      for (const select of this.querySelectorAll('[data-custom-end]')) {
        for (const end of steeringEndOptions(this.#config.bars)) {
          const option = document.createElement('option');
          option.value = end.id;
          option.textContent = `${end.joint.title} · ${end.thread} ${end.hand.toUpperCase()}`;
          select.append(option);
        }
      }
      const params = new URLSearchParams(location.search);
      for (const slot of ['track', 'drag']) {
        const id = params.has(slot) ? params.get(slot) : this.#config.defaults?.[slot];
        const select = this.refs.form.elements.namedItem(slot);
        if ([...select.options].some(option => option.value === id && !option.disabled)) select.value = id;
      }
      this.refs.form.hidden = false;
      this.querySelector('[data-steering-loading]').hidden = true;
      this.addEventListener('change', this.#onChange, { signal: this.#controller.signal });
      this.addEventListener('input', this.#onInput, { signal: this.#controller.signal });
      this.refs.form.addEventListener('submit', this.#onSubmit, { signal: this.#controller.signal });
      this.#render();
    } catch {
      this.querySelector('[data-steering-loading]').textContent = this.dataset.loadError;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  #onInput = event => {
    if (event.target instanceof HTMLInputElement) this.#render();
  };

  #onChange = event => {
    if (this.#busy) return;
    if (event.target instanceof HTMLSelectElement) {
      const slot = event.target.name;
      if (slot === 'track' || slot === 'drag') {
        for (const type of ['nuts', 'joints', 'spacers']) {
          const checkbox = this.refs.form.elements.namedItem(`${slot}_${type}`);
          if (checkbox instanceof HTMLInputElement) checkbox.checked = false;
        }
        for (const input of this.querySelector(`[data-slot="${slot}"] [data-custom-details]`).querySelectorAll('input, select')) {
          if (input.type === 'checkbox') input.checked = false;
          else input.value = '';
        }
      }
    }
    this.refs.status.textContent = '';
    this.#render();
  };

  #read() {
    for (const slot of ['track', 'drag']) {
      const select = this.refs.form.elements.namedItem(slot);
      this.#selections[slot] = { id: select.value };
      for (const type of ['nuts', 'joints', 'spacers']) {
        const checkbox = this.refs.form.elements.namedItem(`${slot}_${type}`);
        this.#selections[slot][type] = checkbox instanceof HTMLInputElement && checkbox.checked;
      }
      if (select.value === `custom_${slot}`) {
        this.#selections[slot].custom = {};
        for (const key of ['length', 'basis', 'end_a', 'end_b', 'joint_a', 'joint_b', 'nut_a', 'nut_b']) {
          const input = this.refs.form.elements.namedItem(`${slot}_custom_${key}`);
          this.#selections[slot].custom[key] = input.type === 'checkbox' ? input.checked : input.value;
        }
      }
    }
    this.#selections.damper = { id: this.refs.form.elements.namedItem('damper_bracket').value, chassis: this.refs.form.elements.namedItem('damper_chassis').checked };
    this.#selections.boltKit = this.refs.form.elements.namedItem('offroad_bolt_kit').checked;
    return buildSteeringOrder(this.#config.bars, this.#selections, Number(this.refs.form.elements.namedItem('quantity').value), { brackets: this.#config.brackets, chassis: this.#config.chassis, boltKit: this.#config.boltKit });
  }

  #money(amount) {
    return new Intl.NumberFormat(this.#config.locale, { style: 'currency', currency: this.#config.currency }).format(amount / 100);
  }

  #message(code, title = '') {
    return (this.#config.strings[code] || this.#config.strings.missing).replace('__title__', title);
  }

  #render() {
    const order = this.#read();
    for (const slot of ['track', 'drag']) {
      const card = this.querySelector(`[data-slot="${slot}"]`);
      const bar = order.selected.find(item => item.slot === slot);
      const details = card.querySelector('[data-bar-details]');
      details.hidden = !bar || bar.custom;
      const custom = card.querySelector('[data-custom-details]');
      custom.hidden = !bar?.custom;
      for (const input of custom.querySelectorAll('input, select')) {
        input.disabled = this.#busy || !bar?.custom;
        if (input.type !== 'checkbox') input.required = Boolean(bar?.custom);
      }
      if (bar?.custom) {
        for (const position of ['a', 'b']) {
          const end = steeringEndOptions(this.#config.bars).find(end => end.id === this.#selections[slot].custom[`end_${position}`]);
          const row = custom.querySelector(`[data-custom-nut="${position}"]`);
          row.hidden = !end || end.nutsIncluded;
          if (row.hidden) row.querySelector('input').checked = false;
          custom.querySelector(`[data-custom-included="${position}"]`).hidden = !end?.nutsIncluded;
        }
        continue;
      }
      if (!bar) continue;
      card.querySelector('[data-bar-spec]').textContent = `${bar.thread} · ${bar.parts.bar?.sku || ''}`;
      card.querySelector('[data-bar-price]').textContent = this.#money(bar.parts.bar?.price || 0);
      const availability = card.querySelector('[data-bar-stock]');
      availability.textContent = bar.parts.bar?.available ? (bar.parts.bar.preorder ? this.#config.strings.preorder : this.#config.strings.in_stock) : this.#config.strings.sold_out;
      const photo = card.querySelector('[data-bar-photo]');
      photo.hidden = !bar.photo;
      if (bar.photo) { photo.src = bar.photo; photo.alt = bar.label; }
      for (const type of ['nuts', 'joints', 'spacers']) {
        const row = card.querySelector(`[data-option="${type}"]`);
        const checkbox = row.querySelector('input');
        const required = type === 'spacers' ? (bar.offroad ? [bar.parts.spacers] : []) : type === 'nuts' ? [bar.parts.nut_lh, bar.parts.nut_rh] : [bar.jointLhQty ? bar.parts.joint_lh : null, bar.jointRhQty ? bar.parts.joint_rh : null].filter(Boolean);
        const offered = type === 'nuts' ? !bar.nutsIncluded && required.every(Boolean) : required.length > 0 && required.every(Boolean);
        row.hidden = !offered;
        if (!offered) checkbox.checked = false;
        const available = required.every(part => part?.available);
        checkbox.disabled = this.#busy || (!available && !checkbox.checked);
        const amount = type === 'spacers' ? (bar.parts.spacers?.price || 0) * 2 : type === 'joints' ? (bar.parts.joint_lh?.price || 0) * bar.jointLhQty + (bar.parts.joint_rh?.price || 0) * bar.jointRhQty : required.reduce((sum, part) => sum + (part?.price || 0), 0);
        row.querySelector('[data-option-price]').textContent = available ? `+ ${this.#money(amount)}` : this.#config.strings.sold_out;
        if (type === 'joints') row.querySelector('[data-option-detail]').textContent = [bar.jointLhQty ? `${bar.jointLhQty} LH` : '', bar.jointRhQty ? `${bar.jointRhQty} RH` : ''].filter(Boolean).join(' + ') + (bar.nutsIncluded ? ` · ${this.#config.strings.nuts_included}` : '');
      }
      card.querySelector('[data-jimny-note]').hidden = !bar.nutsIncluded;
      card.querySelector('[data-offroad-note]').hidden = !bar.offroad;
      card.querySelector('[data-nuts-note]').hidden = bar.nutsIncluded;
    }
    const hasOffroad = order.selected.some(bar => bar.offroad);
    this.querySelector('[data-offroad-bolts]').hidden = !hasOffroad;
    const boltCheckbox = this.refs.form.elements.namedItem('offroad_bolt_kit');
    if (!hasOffroad) boltCheckbox.checked = false;
    boltCheckbox.disabled = this.#busy || (!this.#config.boltKit?.available && !boltCheckbox.checked);
    this.querySelector('[data-bolt-price]').textContent = this.#config.boltKit?.available ? `+ ${this.#money(this.#config.boltKit.price)}` : this.#config.strings.sold_out;
    const damperSelect = this.refs.form.elements.namedItem('damper_bracket');
    damperSelect.disabled = this.#busy;
    for (const option of damperSelect.options) {
      if (!option.value) continue;
      const bracket = this.#config.brackets.find(item => item.id === option.value);
      option.disabled = !bracket?.part?.available;
      option.textContent = `${bracket.label} — ${bracket.part?.available ? this.#money(bracket.part.price) : this.#config.strings.sold_out}`;
    }
    const chassisCheckbox = this.refs.form.elements.namedItem('damper_chassis');
    chassisCheckbox.disabled = this.#busy || (!this.#config.chassis?.available && !chassisCheckbox.checked);
    this.querySelector('[data-chassis-price]').textContent = this.#config.chassis?.available ? `+ ${this.#money(this.#config.chassis.price)}` : this.#config.strings.sold_out;
    const current = this.#read();
    const fragment = document.createDocumentFragment();
    for (const line of current.lines) {
      const item = document.createElement('li');
      const name = document.createElement('span');
      name.textContent = `${line.quantity} × ${line.title}`;
      const price = document.createElement('strong');
      price.textContent = this.#money(line.price * line.quantity);
      item.append(name, price);
      fragment.append(item);
    }
    if (!current.lines.length) {
      const item = document.createElement('li');
      item.textContent = this.#config.strings.empty_summary;
      fragment.append(item);
    }
    this.refs.summary.replaceChildren(fragment);
    this.refs.total.textContent = this.#money(current.total);
    this.querySelector('[data-custom-pending]').hidden = !current.selected.some(bar => bar.custom && !bar.parts.bar?.id);
    const specs = this.querySelector('[data-custom-specs]');
    specs.hidden = !current.selected.some(bar => bar.custom);
    specs.textContent = Object.entries(current.customProperties).map(([name, value]) => `${name}: ${value}`).join('\n');
    this.refs.submitButton.disabled = this.#busy || this.#uncertain || current.issues.length > 0;
    this.refs.submitButton.textContent = this.#busy ? this.#config.strings.adding : this.#config.strings.add_to_cart;
    const issue = current.issues.find(item => item.code !== 'empty');
    this.querySelector('[data-steering-issue]').textContent = issue ? this.#message(issue.code, issue.title) : '';
    for (const input of this.refs.form.querySelectorAll('select[name="track"], select[name="drag"], input[name="quantity"]')) input.disabled = this.#busy;
  }

  async #getCart() {
    const response = await fetch(`${this.#config.root}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal: this.#controller.signal });
    if (!response.ok) throw new Error(this.#config.strings.connection_error);
    return response.json();
  }

  async #refreshProducts(order) {
    const products = await Promise.all([...new Set(order.lines.map(line => line.handle))].map(async handle => {
      const response = await fetch(`${this.#config.root}products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin', cache: 'no-store', signal: this.#controller.signal });
      if (!response.ok) throw new Error(this.#config.strings.connection_error);
      return response.json();
    }));
    let changed = false;
    const parts = [...this.#config.bars.flatMap(bar => Object.values(bar.parts)), ...(this.#config.brackets || []).map(bracket => bracket.part), this.#config.chassis, this.#config.boltKit];
    for (const part of parts) {
        if (!part) continue;
        const product = products.find(item => item.handle === part.handle);
        if (!product) continue;
        const variant = product.variants.find(item => Number(item.id) === Number(part.id));
        if (!variant) { part.available = false; continue; }
        if (part.price !== variant.price) changed = true;
        part.price = variant.price;
        part.available = variant.available;
        if (variant.quantity_rule) part.quantityRule = variant.quantity_rule;
    }
    return changed;
  }

  #syncCart(cart, sections = {}) {
    const event = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [], promise: event.promise }));
    event.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, sections, source: 'dor-steering-builder', itemCount: cart.item_count } });
  }

  #onSubmit = async event => {
    event.preventDefault();
    if (this.#busy || this.#uncertain || !this.refs.form.reportValidity()) return;
    let order = this.#read();
    if (order.issues.length) return;
    this.#busy = true;
    this.#render();
    this.refs.status.textContent = this.#config.strings.checking;
    let before;
    let sent = false;
    try {
      const changed = await this.#refreshProducts(order);
      order = this.#read();
      if (order.issues.length) { this.refs.status.textContent = this.#message(order.issues[0].code, order.issues[0].title); return; }
      if (changed) { this.refs.status.textContent = this.#config.strings.price_changed; return; }
      before = await this.#getCart();
      const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map(element => element.dataset.sectionId).filter(Boolean))].slice(0, 5);
      sent = true;
      const response = await fetch(`${this.#config.root}cart/add.js`, {
        method: 'POST', credentials: 'same-origin', signal: this.#controller.signal,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: order.items, sections, sections_url: location.pathname })
      });
      const result = await response.json();
      const after = await this.#getCart();
      this.#syncCart(after, result.sections || {});
      const reconciliation = reconcileSteeringCart(before, after, order.items);
      if (!response.ok || result.status || !reconciliation.complete) {
        this.#uncertain = reconciliation.changed;
        this.refs.status.textContent = this.#uncertain ? this.#config.strings.partial : (typeof result.description === 'string' ? result.description : this.#config.strings.stock_changed);
        return;
      }
      this.refs.status.textContent = this.#config.strings.added;
      this.querySelector('[data-steering-cart-link]').focus();
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (sent) {
        try {
          const after = await this.#getCart();
          this.#syncCart(after);
          const reconciliation = reconcileSteeringCart(before, after, order.items);
          this.#uncertain = true;
          this.refs.status.textContent = reconciliation.complete ? this.#config.strings.added : this.#config.strings.uncertain;
        } catch {
          this.#uncertain = true;
          this.refs.status.textContent = this.#config.strings.uncertain;
        }
      } else this.refs.status.textContent = this.#config.strings.connection_error;
    } finally {
      this.#busy = false;
      if (this.isConnected) this.#render();
    }
  };
}

if (!customElements.get('dor-steering-builder')) customElements.define('dor-steering-builder', DORSteeringBuilder);

